import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { CustomerItem } from "../../../../mocks/customers";
import type { ShareItem } from "../../../../mocks/animals";
import type { PaymentRecord } from "../../../../mocks/payments";
import { useCustomers } from "../../../../hooks/useCustomers";
import { useAnimals } from "../../../../hooks/useAnimals";
import { usePayments } from "../../../../hooks/usePayments";
import { useBankTransfers } from "../../../../hooks/useBankTransfers";
import { useUsers } from "../../../../hooks/useUsers";
import {
  analyzeCustomerDeletion,
  deleteCustomerCascade,
  getShareTotal,
  recalcAnimalTotals,
  recalcCustomersFromAnimals,
} from "../../../../lib/cascadeOps";
import {
  createGroupedSale,
  findCustomerShares,
} from "../../../../lib/salesFlow";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../../lib/animalLabels";
import CustomerInfoCard from "./components/CustomerInfoCard";
import SaleCard from "./components/SaleCard";
import UserModal, { type UserFormData } from "../../kullanici-yonetimi/components/UserModal";
import type { LinkedShare, SaleEditPayload } from "./components/SaleCard";
import SatisEkleDrawer from "../components/SatisEkleDrawer";
import type { SaleFormData } from "../components/SatisEkleDrawer";
import { normalizePhone, normalizeText } from "../../../../store/usersStore";
import { applyOperationResult } from "../../../../lib/operationRefresh";
import { createSaleOperationRequest } from "../../../../lib/api";

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

export default function MusteriDetayPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const customerId = Number(id);

  const { customers, setAllCustomers } = useCustomers();
  const { animals: animalData, setAllAnimals } = useAnimals();
  const { payments, setAllPayments } = usePayments();
  const { users, setAllUsers } = useUsers();
  const { setAllBankTransfers } = useBankTransfers();

  const [showSatisDrawer, setShowSatisDrawer] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCustomerEditModal, setShowCustomerEditModal] = useState(false);

  const customer = customers.find((item) => item.id === customerId);

  const deleteSummary = useMemo(() => {
    if (!customer) return null;
    return analyzeCustomerDeletion(customerId, customers, animalData, payments);
  }, [animalData, customer, customerId, customers, payments]);

  const linkedShares: LinkedShare[] = useMemo(() => {
    if (!customer) return [];

    return findCustomerShares(customer, animalData).map((item) => ({
      share: item.share,
      animalId: item.animal.id,
      kupeNo: getDisplayKupeNo(item.animal),
      siparisNo: getDisplaySiparisNo(item.animal),
      kesimGunu: item.animal.kesimGunu,
      hisseliSatisEtiketi: item.animal.hisseliSatisEtiketi,
      animalTur: item.animal.tur,
      animalDurum: item.animal.durum,
    }));
  }, [animalData, customer]);

  const toplamBorc = linkedShares.reduce(
    (sum, item) => sum + getShareTotal(item.share),
    0
  );
  const toplamOdenen = linkedShares.reduce(
    (sum, item) => sum + item.share.odenen,
    0
  );
  const toplamHisse = linkedShares.reduce(
    (sum, item) => sum + item.share.hisseSayisi,
    0
  );
  const kalanCount = linkedShares.filter((item) => item.share.kalan > 0).length;

  const linkedUserAccount = useMemo(() => {
    if (!customer) return null;

    return (
      users.find(
        (user) =>
          user.rol === "Müşteri" &&
          (user.customerId === customer.id ||
            normalizePhone(user.telefon) === normalizePhone(customer.telefon) ||
            normalizeText(user.adSoyad) === normalizeText(customer.adSoyad))
      ) ?? null
    );
  }, [customer, users]);
  const unpaidShareIds = linkedShares
    .filter((item) => item.share.kalan > 0)
    .map((item) => item.share.id);

  function buildNextPaymentId(existing: PaymentRecord[]) {
    const maxId = existing.reduce((max, item) => Math.max(max, item.id), 0);
    const candidate = Date.now() * 1000 + Math.floor(Math.random() * 1000);
    return Math.max(maxId + 1, candidate);
  }

  function handleEditSave(shareId: number, payload: SaleEditPayload) {
    const { updates, kaporaTutar, kaporaTip, kaporaDekontNo, kaporaAciklama } = payload;

    const ownerAnimal = animalData.find((animal) =>
      animal.hisseler.some((share) => share.id === shareId)
    );
    const currentShare = ownerAnimal?.hisseler.find((share) => share.id === shareId);

    if (!ownerAnimal || !currentShare || !customer) return;

    const relatedPayments = payments
      .filter((payment) => payment.shareId === shareId)
      .sort((a, b) => {
        if (a.tarih === b.tarih) return a.id - b.id;
        return a.tarih.localeCompare(b.tarih);
      });

    const kaporaPayment = relatedPayments[0] ?? null;
    const otherPayments = kaporaPayment
      ? relatedPayments.filter((payment) => payment.id !== kaporaPayment.id)
      : relatedPayments;
    const otherPaymentsTotal = otherPayments.reduce((sum, payment) => sum + (payment.tutar || 0), 0);
    const safeKapora = Math.max(0, kaporaTutar || 0);
    const nextHisseNo = String(updates.hisseNo ?? currentShare.hisseNo);

    let nextPayments = [...payments];

    if (kaporaPayment) {
      if (safeKapora > 0) {
        nextPayments = nextPayments.map((payment) =>
          payment.id === kaporaPayment.id
            ? {
                ...payment,
                tarih: currentShare.satisTarihi || payment.tarih,
                tutar: safeKapora,
                tip: kaporaTip,
                aciklama: kaporaAciklama.trim(),
                havaleNo: kaporaDekontNo || undefined,
                fisNo: updates.satisFisNo?.trim() || payment.fisNo,
                hisseNo: nextHisseNo,
              }
            : payment.shareId === shareId && Object.prototype.hasOwnProperty.call(updates, "satisFisNo")
            ? { ...payment, fisNo: updates.satisFisNo?.trim() || undefined, hisseNo: nextHisseNo }
            : payment
        );
      } else {
        nextPayments = nextPayments.filter((payment) => payment.id !== kaporaPayment.id);
        if (Object.prototype.hasOwnProperty.call(updates, "satisFisNo")) {
          nextPayments = nextPayments.map((payment) =>
            payment.shareId === shareId
              ? { ...payment, fisNo: updates.satisFisNo?.trim() || undefined, hisseNo: nextHisseNo }
              : payment
          );
        }
      }
    } else if (safeKapora > 0) {
      nextPayments = [
        {
          id: buildNextPaymentId(payments),
          tarih: currentShare.satisTarihi || new Date().toISOString().slice(0, 10),
          musteriAdi: customer.adSoyad,
          telefon: customer.telefon,
          musteriId: customer.id,
          hayvanKupeNo: ownerAnimal.kupeNo,
          hayvanSiparisNo: String(ownerAnimal.siraNo ?? ""),
          animalId: ownerAnimal.id,
          hisseNo: nextHisseNo,
          shareId,
          tutar: safeKapora,
          tip: kaporaTip,
          aciklama: kaporaAciklama.trim(),
          havaleNo: kaporaDekontNo || undefined,
          fisNo: updates.satisFisNo?.trim() || currentShare.satisFisNo || undefined,
          olusturan: "Admin",
        },
        ...nextPayments.map((payment) =>
          payment.shareId === shareId && Object.prototype.hasOwnProperty.call(updates, "satisFisNo")
            ? { ...payment, fisNo: updates.satisFisNo?.trim() || undefined }
            : payment
        ),
      ];
    } else if (Object.prototype.hasOwnProperty.call(updates, "satisFisNo")) {
      nextPayments = nextPayments.map((payment) =>
        payment.shareId === shareId
          ? { ...payment, fisNo: updates.satisFisNo?.trim() || undefined }
          : payment
      );
    }

    const nextOdenen = otherPaymentsTotal + safeKapora;

    const nextAnimals = animalData.map((animal) => {
      const hasTarget = animal.hisseler.some((share) => share.id === shareId);
      if (!hasTarget) return animal;

      const nextAnimal = {
        ...animal,
        hisseler: animal.hisseler.map((share) => {
          if (share.id !== shareId) return share;

          const updated = {
            ...share,
            ...updates,
            pesinatAciklama: kaporaAciklama.trim() || undefined,
            pesinatDekontNo: kaporaDekontNo.trim() || undefined,
            odenen: nextOdenen,
          } as ShareItem;

          return updated;
        }),
      };

      return recalcAnimalTotals(nextAnimal);
    });

    const nextCustomers = recalcCustomersFromAnimals(customers, nextAnimals);

    void setAllPayments(() => nextPayments);
    void setAllAnimals(() => nextAnimals);
    void setAllCustomers(() => nextCustomers);
  }

  async function handleCustomerSave(data: UserFormData) {
    await setAllCustomers((prev) =>
      prev.map((item) =>
        item.id === customerId
          ? {
              ...item,
              adSoyad: data.adSoyad,
              telefon: data.telefon,
              kisaNot: data.kisaNot || undefined,
            }
          : item
      )
    );

    if (linkedUserAccount) {
      await setAllUsers((prev) =>
        prev.map((user) =>
          user.id === linkedUserAccount.id
            ? {
                ...user,
                adSoyad: data.adSoyad,
                telefon: data.telefon,
                email: data.email,
                durum: data.durum,
                forcePasswordChange: data.forcePasswordChange,
                smsOperationalOptIn: data.smsOperationalOptIn,
                smsMarketingOptIn: data.smsMarketingOptIn,
              }
            : user
        )
      );
    }

    setShowCustomerEditModal(false);
  }

  async function handleSatisKaydet(data: SaleFormData) {
    const selectedCustomer = customers.find((item) => item.id === data.customerId);
    if (!selectedCustomer) return;

    try {
      const result = await createSaleOperationRequest(data as unknown as Record<string, unknown>);
      await applyOperationResult(result);
      setShowSatisDrawer(false);
    } catch (error) {
      console.error(error);
      window.alert("Satış kaydı sırasında çakışma veya bağlantı hatası oluştu. Lütfen son veriyi kontrol edip tekrar deneyin.");
    }
  }

  function handleDeleteCustomer() {
    if (!customer) return;

    const result = deleteCustomerCascade({
      customerId: customer.id,
      animals: animalData,
      customers,
      payments,
    });

    setAllAnimals(() => result.animals);
    setAllCustomers(result.customers);
    setAllPayments(result.payments);

    setShowDeleteConfirm(false);
    navigate("/panel/musteriler");
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-farm-cream flex items-center justify-center font-body">
        <div className="text-center px-4">
          <div className="w-12 h-12 flex items-center justify-center mx-auto text-farm-muted mb-3">
            <i className="ri-user-unfollow-line text-4xl" />
          </div>
          <p className="font-heading font-semibold text-farm-brown text-base mb-1">
            Müşteri bulunamadı
          </p>
          <Link
            to="/panel/musteriler"
            className="inline-flex items-center gap-1.5 px-4 py-2 mt-3 rounded-full bg-farm-earth text-white text-xs font-semibold cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm" />
            </div>
            Listeye Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-4xl mx-auto px-3 sm:px-5 py-4">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <h1 className="font-heading font-bold text-base text-farm-brown">
              Müşteri Detayı
            </h1>
            <p className="font-body text-xs text-farm-muted">
              Satış kayıtları ve ödeme geçmişi
              {kalanCount > 0 && (
                <span className="text-farm-earth"> · {kalanCount} açık bakiye</span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => navigate("/panel/musteriler")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-farm-border bg-farm-warm text-farm-muted font-body text-xs font-semibold hover:bg-farm-cream hover:text-farm-brown transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xs" />
              </div>
              <span className="hidden sm:inline">Listeye Dön</span>
            </button>

            {unpaidShareIds.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  navigate("/panel/odeme-al", {
                    state: {
                      customerId: customer.id,
                      selectedShareIds: unpaidShareIds,
                    },
                  })
                }
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-farm-green/25 bg-farm-green-pale text-farm-green font-body text-xs font-semibold hover:bg-farm-green hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-cash-line text-xs" />
                </div>
                <span className="hidden sm:inline">Toplu Ödeme Al</span>
                <span className="sm:hidden">Ödeme</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-500 font-body text-xs font-semibold hover:bg-red-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-delete-bin-line text-xs" />
              </div>
              <span className="hidden sm:inline">Sil</span>
            </button>

            <button
              type="button"
              onClick={() => setShowSatisDrawer(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-farm-earth text-white font-body text-xs font-semibold hover:bg-farm-brown transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line text-xs" />
              </div>
              <span className="hidden sm:inline">Satış Ekle</span>
              <span className="sm:hidden">Satış</span>
            </button>
          </div>
        </div>

        <CustomerInfoCard
          customer={customer}
          toplamHisse={toplamHisse}
          toplamBorc={toplamBorc}
          toplamOdenen={toplamOdenen}
          hasLoginAccount={Boolean(linkedUserAccount)}
          onEditClick={() => setShowCustomerEditModal(true)}
        />

        <div className="flex items-center justify-between mb-2.5 flex-wrap gap-2">
          <h2 className="font-heading font-semibold text-sm text-farm-brown">
            Satış Kayıtları
            <span className="ml-2 font-body text-xs text-farm-muted font-normal">
              ({linkedShares.length} kayıt)
            </span>
          </h2>

          {linkedShares.length > 0 && (
            <div className="flex items-center gap-1.5 font-body text-[10px] text-farm-muted">
              <span className="text-farm-green font-semibold">
                {fmt(toplamOdenen)}
              </span>
              <span>/</span>
              <span className="text-farm-brown font-semibold">
                {fmt(toplamBorc)}
              </span>
            </div>
          )}
        </div>

        {linkedShares.length === 0 ? (
          <div className="bg-farm-warm border border-dashed border-farm-border rounded-farm py-10 text-center">
            <div className="w-10 h-10 flex items-center justify-center mx-auto text-farm-muted mb-2">
              <i className="ri-file-list-3-line text-3xl" />
            </div>
            <p className="font-body text-sm text-farm-muted">
              Satış kaydı bulunamadı
            </p>
            <p className="font-body text-xs text-farm-muted/60 mt-1">
              Bu müşteri henüz hisse almamış
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {linkedShares.map((item) => (
              <SaleCard
                key={item.share.id}
                linked={item}
                customerId={customer.id}
                onEditSave={handleEditSave}
              />
            ))}
          </div>
        )}

        <div className="h-6" />
      </div>

      {showSatisDrawer && (
        <SatisEkleDrawer
          customer={customer}
          onClose={() => setShowSatisDrawer(false)}
          onSave={handleSatisKaydet}
        />
      )}

      {showCustomerEditModal && (
        <UserModal
          mode="edit"
          forceRole="Müşteri"
          titleOverride="Müşteri Kaydını Düzenle"
          subtitleOverride="Kullanıcı Yönetimi ile aynı düzenleme formu"
          user={{
            adSoyad: customer.adSoyad,
            telefon: customer.telefon,
            email: linkedUserAccount?.email,
            rol: "Müşteri",
            durum: linkedUserAccount?.durum ?? "Aktif",
            kisaNot: customer.kisaNot,
            forcePasswordChange: linkedUserAccount?.forcePasswordChange ?? true,
            smsOperationalOptIn: linkedUserAccount?.smsOperationalOptIn ?? null,
            smsMarketingOptIn: linkedUserAccount?.smsMarketingOptIn ?? null,
          }}
          onClose={() => setShowCustomerEditModal(false)}
          onSave={handleCustomerSave}
        />
      )}

      {showDeleteConfirm && deleteSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-sm">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-3">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>

            <p className="text-sm font-semibold text-gray-800 text-center mb-1">
              Müşteri silinsin mi?
            </p>
            <p className="text-xs text-gray-500 text-center mb-4">
              Bu işlem geri alınamaz.
            </p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-3 mb-4 text-xs text-gray-700 space-y-1">
              <p>
                <span className="font-semibold">Müşteri:</span> {customer.adSoyad}
              </p>
              <p>
                <span className="font-semibold">Silinecek hisse:</span>{" "}
                {deleteSummary.shareCount}
              </p>
              <p>
                <span className="font-semibold">Silinecek ödeme:</span>{" "}
                {deleteSummary.paymentCount}
              </p>
              <p>
                <span className="font-semibold">İade / ödenen toplam:</span>{" "}
                {fmt(deleteSummary.refundTotal)}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                type="button"
                className="flex-1 py-2 text-sm border-2 border-gray-200 text-gray-600 font-medium rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                İptal
              </button>

              <button
                onClick={handleDeleteCustomer}
                type="button"
                className="flex-1 py-2 text-sm bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
              >
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}